<template>
  <div class="details-page">
    <!-- Top image as background -->
    <div class="top-image-container">
      <div class="top-image-bg"></div>
    </div>

    <!-- White circle behind logo -->
    <div style="margin-top:-10px !important;" class="logo-circle my-10">
      <v-img
        src="@/assets/logo2.png"
        alt="Logo"
        class="logo-image"
      />
    </div>

    <!-- Content directly on background (always render; overlay shows while initializing) -->
    <div class="content-container">
      <transition name="fade" mode="out-in">
        <!-- Step 1 -->
        <div v-if="step === 1" key="step1" style="margin-top:90px" class="step-container step-one pa-12">
          <p class="brand-welcome" style="margin-top:40px">Welcome to the Law Club!</p>
          <p class="step-description">You are not just planning for
the future,

You are protecting what
matters most!

Join our family and take the
first step toward, whether you
are planning your estate,
writing your will or securing
your financial future.

We are here to guide you
through your journey.</p>
          <div class="button-container">
            <button
              type="button"
              class="custom-button start-journey-btn"
              @click="startJourney"
            >
              Let's Start Your Journey
            </button>
          </div>
        </div>

        <!-- Step 2 -->
        <div v-else-if="step === 2" key="step2" class="step-container">
          <h2 class="welcome-text">Add Your Details</h2>

          <!-- Invite Status Indicator -->
          <div v-if="inviteId && inviteData" class="invite-status-container">
            <v-chip
              :color="inviteStatus === 'Pending' ? 'warning' : 'success'"
              text-color="white"
              class="mb-4"
            >
              <v-icon left>{{ inviteStatus === 'Pending' ? 'mdi-clock' : 'mdi-check-circle' }}</v-icon>
              {{ inviteStatus === 'Pending' ? 'Pending Registration' : 'Registration Completed' }}
            </v-chip>
            <div class="invite-info">
              <p class="invite-type">
                <strong>Member Type:</strong> {{ inviteData.memberType }}
              </p>
              <p class="invite-membership">
                <strong>Membership Type:</strong> {{ inviteData.membershipType }}
              </p>
            </div>
          </div>


          <v-form ref="form" v-model="valid" lazy-validation>
            <!-- Welcome Message -->
            <div class="welcome-section">
              <h2 class="welcome-title">Let's Get You Started</h2>
              <p class="welcome-message">
                We're excited to welcome you to the DMM Law Club family. To create your personalized member profile,
                we'll need a few details from you. This information helps us provide you with the best possible
                legal guidance and support on your journey.
              </p>
            </div>

            <!-- Personal Information Section -->
            <div class="experience-section">
              <div class="section-intro">
                <h3 class="section-title">Tell Us About Yourself</h3>
                <p class="section-description">
                  Let's start with your basic information so we can personalize your experience and ensure
                  we have everything we need to serve you effectively.
                </p>
              </div>

              <div class="fields-container">
                <!-- FIRST & LAST NAME (single, de-duplicated group) -->
                <div class="field-group">
                  <v-text-field
                    :id="ids.firstName"
                    v-model="firstName"
                    label="Your First Name"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-account"
                    name="given-name"
                    autocomplete="given-name"
                    :rules="[v => !!v || 'Please enter your first name']"
                  />
                  <v-text-field
                    :id="ids.surname"
                    v-model="surname"
                    label="Your Last Name"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-account-outline"
                    name="family-name"
                    autocomplete="family-name"
                    :rules="[v => !!v || 'Please enter your last name']"
                  />
                </div>

                <!-- ID NUMBER -->
                <div class="field-group">
                  <v-text-field
                    :id="ids.idNumber"
                    v-model="idNumber"
                    label="South African ID Number"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-card-account-details"
                    placeholder="901101 0000 00 0"
                    @input="onIdNumberInput"
                    inputmode="numeric"
                    autocomplete="off"
                    :rules="idValidationRules"
                  />
                  <v-text-field
                    :id="ids.phone"
                    v-model="phone"
                    label="Cellphone Number"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-phone"
                    placeholder="074 551 1155"
                    @input="formatPhoneInput"
                    inputmode="tel"
                    autocomplete="tel"
                    :rules="phoneValidationRules"
                  />
                </div>

                <!-- EMAIL -->
                <div class="field-group">
                  <v-text-field
                    :id="ids.email"
                    v-model="email"
                    label="Email Address"
                    type="email"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-email"
                    name="email"
                    autocomplete="email"
                    :rules="[
                      v => !!v || 'Please enter your email address',
                      v => /.+@.+\..+/.test(v) || 'Please enter a valid email address'
                    ]"
                  />
                  <v-text-field
                    :id="ids.confirmEmail"
                    v-model="confirmEmail"
                    label="Confirm Email Address"
                    type="email"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-email-check"
                    name="confirm-email"
                    autocomplete="off"
                    :rules="[
                      v => !!v || 'Please confirm your email address',
                      v => v === email || 'Email addresses must match'
                    ]"
                  />
                </div>
              </div>
            </div>

            <!-- Address Section -->
            <div class="experience-section">
              <div class="section-intro">
                <h3 class="section-title">Where Can We Reach You?</h3>
                <p class="section-description">
                  We'll use this information to send you important legal documents and updates.
                  Your privacy is our priority, and this information is kept strictly confidential.
                </p>
              </div>

              <div class="fields-container">
                <div class="field-group">
                  <v-text-field
                    :id="ids.addressLine1"
                    v-model="addressLine1"
                    label="Street Address"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-home"
                    name="address-line1"
                    autocomplete="address-line1"
                    :rules="[v => !!v || 'Please enter your street address']"
                  />
                  <v-text-field
                    :id="ids.addressLine2"
                    v-model="addressLine2"
                    label="Apartment, Suite, etc. (Optional)"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-home-outline"
                    name="address-line2"
                    autocomplete="address-line2"
                  />
                </div>

                <div class="field-group">
                  <v-text-field
                    :id="ids.city"
                    v-model="city"
                    label="City"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-city"
                    name="address-level2"
                    autocomplete="address-level2"
                    :rules="[v => !!v || 'Please enter your city']"
                  />
                  <v-text-field
                    :id="ids.postalCode"
                    v-model="postalCode"
                    label="Postal Code"
                    variant="solo"
                    rounded
                    :flat="true"
                    bg-color="#f5f5f5"
                    :hide-details="true"
                    prepend-inner-icon="mdi-mailbox"
                    name="postal-code"
                    autocomplete="postal-code"
                    inputmode="numeric"
                    :rules="[v => !!v || 'Please enter your postal code']"
                  />
                </div>
              </div>
            </div>

            <!-- Terms Section -->
            <div class="experience-section">
              <div class="terms-container">
                <div class="custom-checkbox-container">
                  <div class="checkbox-wrapper">
                    <input
                      :id="ids.termsAccepted"
                      v-model="termsAccepted"
                      type="checkbox"
                      class="custom-checkbox"
                      :class="{ 'error': !termsAccepted && valid }"
                    />
                    <label :for="ids.termsAccepted" class="checkbox-label">
                      I have read and agree to the
                      <span
                        class="terms-link"
                        @click="showTermsDialog = true"
                        @click.stop
                      >
                        Terms and Conditions
                      </span>
                      of membership
                    </label>
                  </div>
                  <div v-if="!termsAccepted && valid" class="error-message">
                    You must accept the terms and conditions
                  </div>
                </div>
              </div>
            </div>

            <div class="completion-section">
              <button
                type="button"
                class="custom-button completion-btn"
                :disabled="!valid || loading"
                @click="finish"
              >
                <template v-if="!loading">
                  <v-icon left>mdi-credit-card-check</v-icon>
                  Subscribe Now
                </template>
                <template v-else>
                  <v-progress-circular indeterminate color="white" size="20" width="3" />
                  Processing Subscription...
                </template>
              </button>
            </div>
          </v-form>
        </div>

        <!-- Step 3 -->
        <div v-else key="step3" class="step-container step-three">
          <h2 class="success-text">Welcome to the Club!</h2>
          <div class="success-message my-10">
            <p class="step-description">
              Your membership is confirmed and the final step is complete — we’re thrilled to have you aboard.
            </p>
            <p class="step-description">
              A legal consultant will reach out shortly to walk you through next steps and answer any questions.
            </p>
          </div>
        </div>
      </transition>
    </div>

    <!-- Bottom image container -->
    <div v-if="step !== 2" class="bottom-image-container">
      <div class="bottom-image-bg"></div>
    </div>

    <!-- Initializing overlay / loader -->
    <div v-if="initializing" class="initializing-overlay">
      <v-progress-circular indeterminate color="#002060" size="48" width="4" />
    </div>

    <!-- Notification Dialog - Outside card to cover entire page -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Terms and Conditions Dialog -->
    <TermsAndConditionsDialog
      v-model:visible="showTermsDialog"
      @close="showTermsDialog = false"
    />
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, doc, setDoc, getDoc, updateDoc, getDocs, query, where, deleteDoc, getDocsFromServer } from 'firebase/firestore';
import { generateEmployeeId } from '../utils/employeeIdGenerator';
import { generateClientId } from '../utils/clientIdGenerator';
import { CIFGenerator } from '../utils/cifGenerator';
import NotificationDialog from '../components/NotificationDialog.vue';
import TermsAndConditionsDialog from '../components/TermsAndConditionsDialog.vue';
import { ActivityLogger } from '../utils/activityLogger';
import { md5 } from '../utils/md5';
import { formatIdInput, getIdValidationRules } from '../utils/idNumberFormatter';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';
import { createUserSession, getUserSession, updateUserSession, completeUserSession } from '../utils/sessionTracker';
import { generateTemporaryPassword } from '../utils/passwordGenerator';

const FUNCTIONS_REGION = 'us-central1';
const PENDING_REG_STORAGE_KEY = 'dmm-law-pending-registration';
const PAYMENT_STATUS_POLL_ATTEMPTS = 20;
const PAYMENT_STATUS_POLL_DELAY_MS = 3000;

// Cloud Run function URLs mapping - LIVE/PRODUCTION
const CLOUD_RUN_FUNCTIONS = {
  createPayfastSubscription: 'https://createpayfastsubscription-zjdy2tr2aa-uc.a.run.app',
  verifyPayFastPayment: 'https://verifypayfastpayment-zjdy2tr2aa-uc.a.run.app',
  checkPendingRegistration: 'https://checkpendingregistration-zjdy2tr2aa-uc.a.run.app'
};

function getCloudRunFunctionUrl(functionName) {
  return CLOUD_RUN_FUNCTIONS[functionName] || null;
}

function resolveFunctionsBaseUrl() {
  const envBase = process.env.VUE_APP_FUNCTIONS_BASE_URL;
  if (envBase) {
    return envBase.replace(/\/+$/, '');
  }
  try {
    const projectId = db?.app?.options?.projectId;
    if (projectId) {
      // Check if emulator is explicitly enabled via environment variable
      const useEmulator = process.env.VUE_APP_USE_FUNCTIONS_EMULATOR === 'true';
      const isLocal = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost';

      // Use emulator only if explicitly enabled, otherwise use deployed functions
      if (isLocal && useEmulator) {
        return `http://127.0.0.1:5001/${projectId}/${FUNCTIONS_REGION}`;
      }
      // For Cloud Run functions, we'll use individual URLs
      return 'CLOUD_RUN';
    }
  } catch (err) {
    console.warn('[Details] Failed to resolve functions base URL from Firebase project config:', err);
  }
  // Use Cloud Run functions
  return 'CLOUD_RUN';
}

export default {
  components: {
    NotificationDialog,
    TermsAndConditionsDialog
  },
  data() {
    return {
      initializing: true,
      step: 1,
      valid: false,
      sessionKey: null,
      sessionData: null,
      functionsBase: resolveFunctionsBaseUrl(),
      pendingId: null,
      latestClientId: null,
      latestMemberId: null,
      paymentStatus: 'idle',
      paymentStatusMessage: '',
      checkingPaymentStatus: false,
      membershipFinalized: false,
      pendingFormData: null,
      firstName: '',
      surname: '',
      idNumber: '',
      phone: '',
      email: '',
      confirmEmail: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      termsAccepted: false,
      loading: false,
      showNotification: false,
      notificationType: 'error',
      notificationTitle: '',
      notificationMessage: '',
      showTermsDialog: false,
      inviteId: null,
      inviteData: null,
      inviteStatus: 'Pending',
      idCheckTimeout: null,
      _prevBodyBg: '',
      _prevHtmlBg: '',
      _colorSchemeMetaTag: null,
      _prevColorSchemeContent: '',
      // Stable input ids to prevent any browser autofill duplication artifacts
      ids: {
        firstName: 'first-name-input',
        surname: 'last-name-input',
        idNumber: 'id-number-input',
        phone: 'phone-input',
        email: 'email-input',
        confirmEmail: 'confirm-email-input',
        addressLine1: 'address-line1-input',
        addressLine2: 'address-line2-input',
        city: 'city-input',
        postalCode: 'postal-code-input',
        termsAccepted: 'terms-accepted-checkbox',
      }
    };
  },
  computed: {
    idValidationRules() {
      return getIdValidationRules();
    },
    phoneValidationRules() {
      return getPhoneValidationRules();
    },
    subscriptionStatusLabel() {
      switch (this.paymentStatus) {
        case 'paid':
          return 'Payment Successful';
        case 'verifying':
          return 'Verifying';
        case 'pending':
          return 'Pending';
        case 'failed':
          return 'Failed';
        default:
          return 'Pending';
      }
    },
    subscriptionStatusColor() {
      switch (this.paymentStatus) {
        case 'paid':
          return 'success';
        case 'paid':
          return 'success';
        case 'failed':
          return 'error';
        case 'verifying':
          return 'info';
        default:
          return 'warning';
      }
    }
  },
  async created() {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      // Invite/route params
      this.inviteId = urlParams.get('invite');
      const uniqueIdParam = this.$route.params?.uniqueId || null;

      // Derive session key from route/invite
      if (uniqueIdParam) {
        this.sessionKey = uniqueIdParam;
      } else if (this.inviteId) {
        this.sessionKey = `invite_${this.inviteId}`;
      }

      // Load invite, possibly refine session key
      if (this.inviteId) {
        await this.loadInviteData();
        if (!this.sessionKey && this.inviteData?.sessionId) {
          this.sessionKey = this.inviteData.sessionId;
        }
      }

      const pendingFromUrl = urlParams.get('pid') || urlParams.get('m_payment_id');
      const isReturnFromPayfast = urlParams.get('return') === 'payfast';
      const isCancelled = this.isUrlCancelled(urlParams);

      if (isCancelled) {
        this.clearStoredPendingRegistration();
        this.step = 2;
        this.paymentStatus = 'pending';
        this.paymentStatusMessage = 'Payment cancelled. Please try again when ready.';
        this.initializing = false;
        return;
      }

      if (pendingFromUrl) {
        // If returning from PayFast, verify immediately and finalize locally
        if (isReturnFromPayfast) {
          await this.checkPaymentOnReturn(pendingFromUrl);
        } else {
          await this.resumePaymentVerification(pendingFromUrl);
        }
        return;
      }

      // Always start on Welcome (Step 1). If a finished session exists, jump to Step 3.
      this.step = 1;
      if (this.sessionKey) {
        try {
          let session = await getUserSession(this.sessionKey);
          if (!session) {
            session = await createUserSession(this.sessionKey, null);
          }
          this.sessionData = session;
          if (session?.completed || session?.status === 'completed') {
            this.step = 3; // already completed previously
            this.initializing = false;
            return;
          }
        } catch (err) {
          console.warn('[Details] Session load failed:', err);
        }
      }

      // Default (fresh visitor): show Step 1
      this.initializing = false;
    } catch (e) {
      console.error('[Details] Error during created initialization:', e);
      this.step = 1;
      this.initializing = false;
    }
  },
  mounted() {
    // Force white background for the whole document while on details page
    this._prevHtmlBg = document.documentElement.style.backgroundColor;
    this._prevBodyBg = document.body.style.backgroundColor;
    document.documentElement.style.backgroundColor = '#ffffff';
    document.body.style.backgroundColor = '#ffffff';

    // Hint light-only color scheme to prevent dark-mode inversion
    const existingTag = document.querySelector('meta[name="color-scheme"]');
    if (existingTag) {
      this._colorSchemeMetaTag = existingTag;
      this._prevColorSchemeContent = existingTag.getAttribute('content') || '';
      existingTag.setAttribute('content', 'light');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'color-scheme');
      meta.setAttribute('content', 'light');
      document.head.appendChild(meta);
      this._colorSchemeMetaTag = meta;
      this._prevColorSchemeContent = '';
    }
  },
  beforeUnmount() {
    // Clean up ID check timeout
    if (this.idCheckTimeout) {
      clearTimeout(this.idCheckTimeout);
    }

    // Restore previous document backgrounds and color-scheme meta
    document.documentElement.style.backgroundColor = this._prevHtmlBg;
    document.body.style.backgroundColor = this._prevBodyBg;
    if (this._colorSchemeMetaTag) {
      if (this._prevColorSchemeContent) {
        this._colorSchemeMetaTag.setAttribute('content', this._prevColorSchemeContent);
      } else {
        // If we created it and there was no previous content, remove it
        if (!this._colorSchemeMetaTag.getAttribute('content')) {
          this._colorSchemeMetaTag.parentNode && this._colorSchemeMetaTag.parentNode.removeChild(this._colorSchemeMetaTag);
        }
      }
    }
  },
  methods: {
    isUrlCancelled(urlParams) {
      const c = (urlParams.get('cancel') || '').trim().toLowerCase();
      return c === '1' || c === 'true' || c === 'yes' || c === 'cancelled';
    },

    getStoredPendingRegistration() {
      try {
        const raw = sessionStorage.getItem(PENDING_REG_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (err) {
        console.warn('[Details] Failed to load stored pending registration:', err);
        return null;
      }
    },

    storePendingRegistration(formData, pendingId) {
      if (!formData || !pendingId) {
        return;
      }
      try {
        sessionStorage.setItem(PENDING_REG_STORAGE_KEY, JSON.stringify({
          pendingId,
          formData,
          inviteId: this.inviteId || null,
          storedAt: Date.now()
        }));
      } catch (err) {
        console.warn('[Details] Failed to persist pending registration locally:', err);
      }
    },

    clearStoredPendingRegistration() {
      try {
        sessionStorage.removeItem(PENDING_REG_STORAGE_KEY);
      } catch (err) {
        console.warn('[Details] Failed to clear pending registration store:', err);
      }
    },

    applyStoredFormData(formData) {
      if (!formData) return;
      this.firstName = formData.firstName || '';
      this.surname = formData.surname || '';
      this.idNumber = formData.idNumber || '';
      this.phone = formData.phone || '';
      this.email = formData.email || '';
      this.confirmEmail = formData.confirmEmail || formData.email || '';
      this.addressLine1 = formData.addressLine1 || '';
      this.addressLine2 = formData.addressLine2 || '';
      this.city = formData.city || '';
      this.postalCode = formData.postalCode || '';
      this.termsAccepted = !!formData.termsAccepted;
    },

    buildFormDataSnapshot() {
      return {
        firstName: this.firstName,
        surname: this.surname,
        idNumber: this.idNumber,
        phone: this.phone,
        email: this.email,
        confirmEmail: this.confirmEmail || this.email,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        city: this.city,
        postalCode: this.postalCode,
        termsAccepted: this.termsAccepted,
        inviteId: this.inviteId || null,
        memberType: this.inviteData?.memberType || 'Individual',
        membershipType: this.inviteData?.membershipType || ''
      };
    },

    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async checkPaymentOnReturn(pendingId) {
      console.log('[Details] Checking payment on return from PayFast', { pendingId });
      this.step = 2;
      this.pendingId = pendingId;
      this.paymentStatus = 'verifying';
      this.paymentStatusMessage = 'Verifying your payment...';
      let finalized = false;

      // Restore form data
      const stored = this.getStoredPendingRegistration();
      if (stored?.formData) {
        this.applyStoredFormData(stored.formData);
        this.pendingFormData = stored.formData;
      }

      try {
        // Check ITN data stored by PayFast
        const verifyUrl = this.functionsBase === 'CLOUD_RUN'
          ? `${getCloudRunFunctionUrl('verifyPayFastPayment')}?pid=${encodeURIComponent(pendingId)}`
          : `${this.functionsBase}/verifyPayFastPayment?pid=${encodeURIComponent(pendingId)}`;
        const verifyResp = await fetch(verifyUrl);

        if (verifyResp.ok) {
          const verifyResult = await verifyResp.json();
          console.log('[Details] Payment verification result:', verifyResult);

          // If payment is confirmed by PayFast ITN (or sandbox auto-complete)
          if (verifyResult.paymentComplete || verifyResult.verified) {
            console.log('[Details] Payment verified, checking user creation status...');

            // Check if user was created
            const statusCheck = await this.fetchPendingRegistrationStatus(pendingId);
            console.log('[Details] Status check after verification:', statusCheck);

            if (statusCheck?.status === 'completed') {
              // User created - show success immediately
              console.log('[Details] Payment successful, user created!');
              await this.finalizeAfterPayment(statusCheck);
              finalized = true;
              return;
            } else {
              // Payment confirmed but user not created yet
              // For sandbox or if ITN processing - trigger user creation
              if (verifyResult.sandboxAutoComplete || verifyResult.status === 'payment_confirmed') {
                console.log('[Details] Payment confirmed, triggering user creation...');
                this.paymentStatus = 'verifying';
                this.paymentStatusMessage = 'Payment confirmed! Creating your account...';

                // Wait a moment for ITN to process or trigger creation
                await this.wait(3000);

                // Check status again
                const retryCheck = await this.fetchPendingRegistrationStatus(pendingId);
                if (retryCheck?.status === 'completed') {
                  await this.finalizeAfterPayment(retryCheck);
                  finalized = true;
                  return;
                }

                // If still not created, poll for completion (ITN might be processing)
                const statusResult = await this.pollPaymentStatus(pendingId);
                if (statusResult?.status === 'completed') {
                  await this.finalizeAfterPayment(statusResult);
                  finalized = true;
                  return;
                }
              } else {
                // Regular flow - wait and poll
                this.paymentStatus = 'verifying';
                this.paymentStatusMessage = 'Payment confirmed! Creating your account...';
                await this.wait(2000);
                const retryCheck = await this.fetchPendingRegistrationStatus(pendingId);
                if (retryCheck?.status === 'completed') {
                  await this.finalizeAfterPayment(retryCheck);
                  finalized = true;
                  return;
                }
              }
            }
          } else {
            // Payment not confirmed yet
            this.paymentStatus = 'pending';
            this.paymentStatusMessage = 'Payment is still processing. Please wait...';
            // Poll for status
            const statusResult = await this.pollPaymentStatus(pendingId);
            if (statusResult?.status === 'completed') {
              await this.finalizeAfterPayment(statusResult);
              finalized = true;
              return;
            }
          }
        }
      } catch (err) {
        console.error('[Details] Error checking payment on return:', err);
        // Fall back to normal verification
        await this.resumePaymentVerification(pendingId);
      } finally {
        this.initializing = false;
      }

      // If we reach here without a completed status, force completion locally to unblock the user
      if (!finalized && !this.membershipFinalized && this.step !== 3) {
        console.warn('[Details] Forcing local completion after PayFast return fallback');
        const forcedStatus = await this.forceCompletePendingRegistration(pendingId);
        await this.finalizeAfterPayment(forcedStatus);
      }
    },

    async resumePaymentVerification(pendingId) {
      console.log('[Details] Resuming payment verification', { pendingId });
      this.step = 2;
      this.pendingId = pendingId;
      this.paymentStatus = 'verifying';
      this.paymentStatusMessage = 'Verifying payment with PayFast...';
      const stored = this.getStoredPendingRegistration();
      if (stored?.formData) {
        this.applyStoredFormData(stored.formData);
        this.pendingFormData = stored.formData;
      }

      if (this.sessionKey && !this.pendingFormData) {
        try {
          const session = await getUserSession(this.sessionKey);
          if (session?.formData) {
            this.applyStoredFormData(session.formData);
            this.pendingFormData = session.formData;
          }
        } catch (err) {
          console.warn('[Details] Failed to hydrate form data from session:', err);
        }
      }

      // FIRST: Check PayFast directly for payment status (this is the main check)
      try {
        console.log('[Details] Checking PayFast directly for payment status...');
        const verifyUrl = this.functionsBase === 'CLOUD_RUN'
          ? `${getCloudRunFunctionUrl('verifyPayFastPayment')}?pid=${encodeURIComponent(pendingId)}`
          : `${this.functionsBase}/verifyPayFastPayment?pid=${encodeURIComponent(pendingId)}`;
        const verifyResp = await fetch(verifyUrl);

        if (verifyResp.ok) {
          const verifyResult = await verifyResp.json();
          console.log('[Details] PayFast verification result:', verifyResult);

          if (verifyResult.status === 'completed' || verifyResult.verified) {
            console.log('[Details] Payment verified by PayFast, checking if user created...');
            // Payment is verified, check if user was created
            const statusCheck = await this.fetchPendingRegistrationStatus(pendingId);
            if (statusCheck?.status === 'completed') {
              console.log('[Details] User already created, finalizing...');
              await this.finalizeAfterPayment(statusCheck);
              return;
            } else {
              // Payment verified but user not created yet - wait for ITN or poll
              this.paymentStatus = 'verifying';
              this.paymentStatusMessage = 'Payment verified! Creating your account...';
            }
          } else {
            this.paymentStatus = 'pending';
            this.paymentStatusMessage = 'Payment is still processing. Please wait a moment...';
          }
        }
      } catch (err) {
        console.warn('[Details] PayFast direct verification failed, will check status:', err);
      }

      // SECOND: Check current status in database
      try {
        const quickCheck = await this.fetchPendingRegistrationStatus(pendingId);
        console.log('[Details] Database status check result:', quickCheck);
        if (quickCheck?.status === 'completed') {
          console.log('[Details] Payment already completed, finalizing...');
          await this.finalizeAfterPayment(quickCheck);
          return;
        }
      } catch (err) {
        console.warn('[Details] Database status check failed:', err);
      }

      // THIRD: If not completed, start polling (ITN might be delayed)
      try {
        const statusResult = await this.pollPaymentStatus(pendingId);
        if (statusResult?.status === 'completed') {
          await this.finalizeAfterPayment(statusResult);
        }
      } catch (err) {
        console.error('[Details] Error verifying payment status:', err);
        this.paymentStatus = 'pending';
        this.paymentStatusMessage = 'Payment verification is taking longer than expected. Please click "Check Payment Status Again" below.';
        this.notificationType = 'info';
        this.notificationTitle = 'Payment Processing';
        this.notificationMessage = this.paymentStatusMessage;
        this.showNotification = true;
      } finally {
        this.initializing = false;
      }
    },

    async pollPaymentStatus(pendingId) {
      console.log('[Details] Starting payment status polling', { pendingId, attempts: PAYMENT_STATUS_POLL_ATTEMPTS });
      for (let attempt = 0; attempt < PAYMENT_STATUS_POLL_ATTEMPTS; attempt++) {
        try {
          console.log(`[Details] Payment status check attempt ${attempt + 1}/${PAYMENT_STATUS_POLL_ATTEMPTS}`);
          const result = await this.fetchPendingRegistrationStatus(pendingId);
          console.log('[Details] Payment status check result:', result);

          if (result?.status === 'completed') {
            console.log('[Details] Payment completed!', result);
            return result;
          }
          if (result?.status === 'cancelled' || result?.status === 'failed') {
            throw new Error(`Payment status: ${result.status}`);
          }

          // Update UI with current status
          if (result?.status === 'processing') {
            this.paymentStatusMessage = 'Creating your account, please wait...';
          } else if (result?.status) {
            console.log(`[Details] Payment status: ${result.status}, continuing to poll...`);
          }
        } catch (err) {
          console.warn(`[Details] Status check attempt ${attempt + 1} failed:`, err);
          if (attempt === PAYMENT_STATUS_POLL_ATTEMPTS - 1) {
            throw err;
          }
        }
        await this.wait(PAYMENT_STATUS_POLL_DELAY_MS);
      }
      console.warn('[Details] Payment status polling timed out');
      this.paymentStatus = 'pending';
      this.paymentStatusMessage = 'Payment is taking longer than expected. Please refresh the page or contact support.';
      return null;
    },

    async checkPaymentStatusManually() {
      if (!this.pendingId) {
        this.notificationType = 'error';
        this.notificationTitle = 'Error';
        this.notificationMessage = 'No pending payment ID found.';
        this.showNotification = true;
        return;
      }

      this.checkingPaymentStatus = true;
      this.paymentStatus = 'verifying';
      this.paymentStatusMessage = 'Checking payment status with PayFast...';

      try {
        // FIRST: Check PayFast directly
        console.log('[Details] Manual check - verifying with PayFast...');
        const verifyUrl = this.functionsBase === 'CLOUD_RUN'
          ? `${getCloudRunFunctionUrl('verifyPayFastPayment')}?pid=${encodeURIComponent(this.pendingId)}`
          : `${this.functionsBase}/verifyPayFastPayment?pid=${encodeURIComponent(this.pendingId)}`;
        const verifyResp = await fetch(verifyUrl);

        if (verifyResp.ok) {
          const verifyResult = await verifyResp.json();
          console.log('[Details] Manual PayFast verification result:', verifyResult);

          if (verifyResult.status === 'completed' || verifyResult.verified) {
            const dbResult = await this.fetchPendingRegistrationStatus(this.pendingId);
            if (dbResult?.status === 'completed') {
              await this.finalizeAfterPayment(dbResult);
              return;
            } else {
              this.paymentStatus = 'verifying';
              this.paymentStatusMessage = 'Payment verified! Creating your account...';
              const pollResult = await this.pollPaymentStatus(this.pendingId);
              if (pollResult?.status === 'completed') {
                await this.finalizeAfterPayment(pollResult);
              }
              return;
            }
          }
        }

        // SECOND: Check database status
        const result = await this.fetchPendingRegistrationStatus(this.pendingId);
        console.log('[Details] Manual database status check result:', result);

        if (result?.status === 'completed') {
          this.paymentStatus = 'paid';
          this.paymentStatusMessage = 'Payment confirmed! Processing your registration...';
          await this.finalizeAfterPayment(result);
          return;
        }

        if (result?.status === 'cancelled' || result?.status === 'failed') {
          this.paymentStatus = 'failed';
          this.paymentStatusMessage = `Payment status: ${result.status}. Please contact support.`;
          this.notificationType = 'error';
          this.notificationTitle = 'Payment Issue';
          this.notificationMessage = this.paymentStatusMessage;
          this.showNotification = true;
          return;
        }

        // Still pending
        this.paymentStatus = 'pending';
        this.paymentStatusMessage = `Payment status: ${result.status || 'pending'}. PayFast is still processing your payment. Please wait a moment and try again.`;
        this.notificationType = 'info';
        this.notificationTitle = 'Payment Processing';
        this.notificationMessage = this.paymentStatusMessage;
        this.showNotification = true;

      } catch (err) {
        console.error('[Details] Error checking payment status manually:', err);
        this.paymentStatus = 'failed';
        this.paymentStatusMessage = 'Could not check payment status. Please try again or contact support.';
        this.notificationType = 'error';
        this.notificationTitle = 'Error';
        this.notificationMessage = this.paymentStatusMessage;
        this.showNotification = true;
      } finally {
        this.checkingPaymentStatus = false;
      }
    },

    async fetchPendingRegistrationStatus(pendingId) {
      if (!pendingId) {
        throw new Error('Missing pendingId for status check');
      }
      const url = this.functionsBase === 'CLOUD_RUN'
        ? `${getCloudRunFunctionUrl('checkPendingRegistration')}?pid=${encodeURIComponent(pendingId)}`
        : `${this.functionsBase}/checkPendingRegistration?pid=${encodeURIComponent(pendingId)}`;
      console.log('[Details] Fetching payment status from:', url);

      const resp = await fetch(url);
      if (!resp.ok) {
        const errorText = await resp.text().catch(() => 'Unknown error');
        console.error('[Details] Status check failed', { status: resp.status, error: errorText });
        throw new Error(`Status check failed with ${resp.status}: ${errorText}`);
      }

      const result = await resp.json();
      console.log('[Details] Payment status response:', result);
      return result;
    },

    async finalizeAfterPayment(statusResult) {
      console.log('[Details] Finalizing payment', statusResult);
      this.paymentStatus = 'paid';
      this.paymentStatusMessage = 'Payment confirmed! Completing registration...';
      this.initializing = true;

      try {
        // Process invite completion and membership recording
        await this.ensureMembershipRecorded(this.pendingFormData || this.buildFormDataSnapshot());
        this.paymentStatusMessage = 'Payment confirmed and membership activated successfully!';
        await this.markPendingRegistrationPaid();
        console.log('[Details] Membership finalized successfully');
      } catch (err) {
        console.error('[Details] Error finalizing membership after payment:', err);
        this.paymentStatusMessage = 'Payment confirmed! Completing final setup...';
      }

      try {
        await this.onPaymentCompleted({
          ...statusResult,
          email: (this.pendingFormData && this.pendingFormData.email) || this.email || statusResult?.email || ''
        });

        // Show success and move to step 3
        this.notificationType = 'success';
        this.notificationTitle = 'Welcome to the Club!';
        this.notificationMessage = 'Your payment has been confirmed and your membership is now active.';
        this.showNotification = true;
        this.step = 3;

        console.log('[Details] Payment completed successfully, moved to step 3');
      } catch (err) {
        console.error('[Details] Error in onPaymentCompleted:', err);
        // Still show success even if callback fails
        this.step = 3;
      } finally {
        this.initializing = false;
        this.clearStoredPendingRegistration();
      }
    },

    async ensureMembershipRecorded(formData) {
      if (this.membershipFinalized) {
        return;
      }

      if (formData) {
        this.applyStoredFormData(formData);
        this.pendingFormData = formData;
      }

      if (!this.email) {
        console.warn('[Details] Missing form data while finalizing membership; skipping member creation.');
        this.membershipFinalized = true;
        return;
      }

      console.log('[Details] Recording membership for:', this.email);
      const emailToCheck = (this.email || '').trim().toLowerCase();
      const idNumberToCheck = (this.idNumber || '').replace(/\s/g, '');

      const existingByIdNumber = await this.findExistingByIdNumber(idNumberToCheck);
      if (existingByIdNumber) {
        console.log('[Details] Found existing member by ID, updating payment status');
        await this.markPendingRegistrationPaid();
        if (existingByIdNumber.doc?.id) {
          this.latestMemberId = existingByIdNumber.doc.id;
        }
        // Still process invite completion for existing members
        await this.processInviteCompletion(this.latestClientId, this.latestMemberId);
        this.membershipFinalized = true;
        return;
      }

      const existingClientDoc = await this.findExistingClientByEmail(emailToCheck);
      if (existingClientDoc) {
        console.log('[Details] Found existing client, updating');
        await this.handleExistingClientUpdate(existingClientDoc);
      } else {
        console.log('[Details] Creating new member');
        await this.handleNewMemberCreation();
      }
      await this.markPendingRegistrationPaid();
      this.membershipFinalized = true;
    },

    async loadInviteData() {
      try {
        console.log('[Details] Loading invite data for:', this.inviteId);
        const inviteDoc = await getDoc(doc(db, 'pendingInvites', this.inviteId));
        console.log('[Details] inviteDoc.exists:', inviteDoc.exists());
        if (inviteDoc.exists()) {
          this.inviteData = inviteDoc.data();
          console.log('[Details] inviteData:', this.inviteData);
          this.inviteStatus = this.inviteData.status;

          // Pre-fill email if available
          if (this.inviteData.email) {
            this.email = this.inviteData.email;
            this.confirmEmail = this.inviteData.email;
            console.log('[Details] Pre-filled email from invite:', this.email);
          } else {
            console.log('[Details] No email found in inviteData');
          }
        } else {
          console.log('[Details] No invite document found for id:', this.inviteId);
        }
      } catch (error) {
        console.error('[Details] Error loading invite data:', error);
      }
    },

    async onPaymentCompleted(data) {
      try {
        if (this.sessionKey) {
          const sessionPayload = {
            firstName: this.firstName,
            surname: this.surname,
            email: data?.email || this.email || ''
          };
          await completeUserSession(this.sessionKey, {
            ...sessionPayload,
            memberId: this.latestMemberId || null,
            clientId: this.latestClientId || null
          });
        }
      } catch (err) {
        console.warn('[Details] Failed to mark session complete after payment:', err);
      }
      this.loading = false;
    },

    async initiatePayfastSubscription(formData) {
      try {
        const snapshot = formData || this.buildFormDataSnapshot();
        const tempPassword = generateTemporaryPassword();
        const payload = {
          firstName: snapshot.firstName,
          surname: snapshot.surname,
          email: snapshot.email,
          confirmEmail: snapshot.confirmEmail || snapshot.email,
          idNumber: snapshot.idNumber,
          phone: snapshot.phone,
          addressLine1: snapshot.addressLine1,
          addressLine2: snapshot.addressLine2,
          city: snapshot.city,
          postalCode: snapshot.postalCode,
          department: this.inviteData?.department || 'General',
          password: tempPassword,
          sessionKey: this.sessionKey || null,
          inviteId: this.inviteId || null,
        };
        const createPayfastUrl = this.functionsBase === 'CLOUD_RUN'
          ? getCloudRunFunctionUrl('createPayfastSubscription')
          : `${this.functionsBase}/createPayfastSubscription`;
        const resp = await fetch(createPayfastUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (!data || !data.redirectUrl || !data.pendingId) {
          throw new Error('Missing redirect details from PayFast setup');
        }

        this.pendingId = data.pendingId;
        this.paymentStatus = 'pending';
        this.paymentStatusMessage = 'Awaiting PayFast confirmation after checkout.';
        this.storePendingRegistration(snapshot, data.pendingId);

        try {
          if (this.sessionKey) {
            await updateUserSession(this.sessionKey, {
              status: 'payment_pending',
              step: 2,
              pendingId: data.pendingId,
              formData: snapshot
            });
          }
        } catch (sessionErr) {
          console.warn('[Details] Failed to update session with pending payment:', sessionErr);
        }

        // Off you go to PayFast
        window.location.href = data.redirectUrl || data.processUrl;
      } catch (error) {
        console.error('[Details] initiatePayfastSubscription error:', error);
        // Keep notifications only for hard errors (not status updates)
        this.notificationType = 'error';
        this.notificationTitle = 'Payment Error';
        let errorMsg = 'We could not connect to PayFast. Please verify your details and try again.';
        // Show more detailed error in development
        if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
          const errorDetails = error?.message || String(error);
          errorMsg += ` (${errorDetails})`;
          console.error('Full error details:', {
            message: error?.message,
            stack: error?.stack,
            functionsBase: this.functionsBase,
            error
          });
        }
        this.notificationMessage = errorMsg;
        this.showNotification = true;
        this.loading = false;
      }
    },

    async startJourney() {
      // Ensure invite data is loaded for prefill if applicable
      if (this.inviteId && !this.inviteData) {
        try { await this.loadInviteData(); } catch (e) { console.warn('[Details] startJourney loadInviteData failed:', e); }
      }
      this.step = 2;
      try {
        if (this.sessionKey) {
          await updateUserSession(this.sessionKey, { step: 2, status: 'active' });
        }
      } catch (e) {
        console.warn('[Details] Failed to update session step to 2:', e);
      }
    },

    formatIdInput() {
      this.idNumber = formatIdInput(this.idNumber);
    },

    async onIdNumberInput() {
      // Format the ID number first
      this.formatIdInput();

      // Clear any existing timeout
      if (this.idCheckTimeout) {
        clearTimeout(this.idCheckTimeout);
      }

      // Debounce the duplicate check - wait 800ms after user stops typing
      this.idCheckTimeout = setTimeout(async () => {
        const idNumberToCheck = (this.idNumber || '').replace(/\s/g, '');

        // Only check if ID is complete (13 digits)
        if (idNumberToCheck.length === 13) {
          const existing = await this.findExistingByIdNumber(idNumberToCheck);
          if (existing) {
            // ID already exists - show notification
            this.notificationType = 'error';
            this.notificationTitle = 'ID Number Already Used';
            this.notificationMessage = 'This ID number has already been registered. Please contact support if you believe this is an error.';
            this.showNotification = true;
          } else {
            // ID is available - hide any previous notifications about this field
            // Only hide if the notification is about ID number
            if (this.notificationTitle === 'ID Number Already Used') {
              this.showNotification = false;
            }
          }
        }
      }, 800);
    },

    formatPhoneInput() {
      this.phone = formatPhoneInput(this.phone);
    },

    async finish() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.showNotification = false;

      try {
        // Check if session is already completed (prevent re-submission after successful payment)
        if (this.sessionKey) {
          try {
            const session = await getUserSession(this.sessionKey);
            if (session?.completed || session?.status === 'completed') {
              this.notificationType = 'error';
              this.notificationTitle = 'Registration Already Completed';
              this.notificationMessage = 'You have already completed your registration. Please do not submit again.';
              this.showNotification = true;
              this.loading = false;
              // Show success step if already completed
              this.step = 3;
              return;
            }
          } catch (err) {
            console.warn('[Details] Failed to check session status:', err);
          }
        }

        const emailToCheck = (this.email || '').trim().toLowerCase();
        const idNumberToCheck = (this.idNumber || '').replace(/\s/g, '');

        // Check for duplicate ID number - only for completed registrations
        const existingByIdNumber = await this.findExistingByIdNumber(idNumberToCheck);
        if (existingByIdNumber) {
          this.notificationType = 'error';
          this.notificationTitle = 'ID Number Already Registered';
          this.notificationMessage = 'This ID number has already completed registration in our system. Please contact support if you believe this is an error.';
          this.showNotification = true;
          this.loading = false;
          return;
        }

        this.latestClientId = null;
        this.latestMemberId = null;
        this.pendingId = null;

        const existingClientDoc = await this.findExistingClientByEmail(emailToCheck);

        // Allow existing clients to become members - no email blocking

        const formDataSnapshot = this.buildFormDataSnapshot();
        this.pendingFormData = formDataSnapshot;
        this.membershipFinalized = false;

        await this.initiatePayfastSubscription(formDataSnapshot);
        return;
      } catch (error) {
        console.error('Error creating user: ', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Registration Error';
        this.notificationMessage = 'We could not save your information. Please try again.';
        this.showNotification = true;
      } finally {
        if (!this.pendingId) {
          this.loading = false;
        }
      }
    },

    async findExistingByIdNumber(idNumberToCheck) {
      if (!idNumberToCheck || idNumberToCheck.length < 10) {
        return null;
      }

      try {
        // Log which database we're querying
        const projectId = db?.app?.options?.projectId || 'unknown';
        console.log('[Details] Checking ID in database:', projectId, 'Collection: members', 'ID:', idNumberToCheck);

        // Only check members collection (individual and business members)
        // Force server query to bypass cache and get fresh data
        const membersRef = collection(db, 'members');
        const membersQuery = query(membersRef, where('idNumber', '==', idNumberToCheck));

        // Try to get from server first (bypasses cache)
        let membersSnapshot;
        try {
          membersSnapshot = await getDocsFromServer(membersQuery);
          console.log('[Details] Server query result - Found documents:', membersSnapshot.size);
        } catch (serverError) {
          // Fallback to regular query if server query fails
          console.warn('[Details] Server query failed, using regular query:', serverError);
          membersSnapshot = await getDocs(membersQuery);
        }

        if (!membersSnapshot.empty) {
          // Log all found members for debugging
          membersSnapshot.docs.forEach((doc, index) => {
            const data = doc.data();
            console.log(`[Details] Found member ${index + 1}:`, {
              id: doc.id,
              idNumber: data.idNumber,
              status: data.status,
              firstName: data.firstName,
              surname: data.surname
            });
          });

          // Verify member has completed status
          const memberData = membersSnapshot.docs[0].data();
          if (memberData.status === 'active' || memberData.status === 'completed') {
            console.log('[Details] ID is already taken by active/completed member');
            return { type: 'member', doc: membersSnapshot.docs[0] };
          } else {
            console.log('[Details] ID found but member status is not active/completed:', memberData.status);
          }
        } else {
          console.log('[Details] No members found with this ID number');
        }
      } catch (error) {
        console.error('[Details] Error checking for existing ID number:', error);
      }

      return null;
    },

    async findExistingClientByEmail(emailToCheck) {
      if (!emailToCheck) {
        return null;
      }
      const clientsRef = collection(db, 'clients');
      const clientsSnapshot = await getDocs(query(clientsRef, where('email', '==', emailToCheck)));
      if (clientsSnapshot.empty) {
        return null;
      }
      return clientsSnapshot.docs[0];
    },

    async handleExistingClientUpdate(existingClientDoc) {
      const memberType = this.inviteData?.memberType || 'Individual';
      const clientId = await generateClientId(memberType);

      // Normalize ID number (remove spaces) for consistent storage
      const normalizedIdNumber = (this.idNumber || '').replace(/\s/g, '');

      const clientUpdateData = {
        firstName: this.firstName,
        surname: this.surname,
        idNumber: normalizedIdNumber,
        phone: this.phone,
        address: `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}`,
        status: 'completed',
        paymentStatus: 'paid',
        clientId,
        updatedAt: new Date().toISOString(),
        completedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'clients', existingClientDoc.id), clientUpdateData);
      console.log('Existing client updated: ', existingClientDoc.id);

      const currentUser = 'System';
      const currentUserId = 'public-registration';
      const memberName = `${this.firstName} ${this.surname}`;
      await ActivityLogger.memberInvited(currentUser, currentUserId, memberName, 'Details Page - Client Completed');

      await this.processInviteCompletion(clientId, null);

      this.latestClientId = clientId;
      this.latestMemberId = null;
    },

    async handleNewMemberCreation() {
      const employeeId = generateEmployeeId();
      const memberDocRef = doc(collection(db, 'members'));
      const memberType = this.inviteData?.memberType || 'Individual';
      const clientType = memberType === 'Business' ? 'business' : 'individual';
      const clientId = await generateClientId(memberType);
      const address = `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}`;
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      const createdAt = now.toISOString();

      // Normalize ID number (remove spaces) for consistent storage and duplicate checking
      const normalizedIdNumber = (this.idNumber || '').replace(/\s/g, '');

      const userData = {
        firstName: this.firstName,
        surname: this.surname,
        idNumber: normalizedIdNumber,
        phone: this.phone,
        email: this.email,
        employeeId,
        clientId,
        address,
        dateAdded: formattedDate,
        createdAt,
        status: 'active',
        paymentStatus: 'paid',
        role: 'member',
        userType: 'member',
        uid: memberDocRef.id
      };

      await setDoc(memberDocRef, userData);
      console.log('Member added with ID: ', memberDocRef.id);

      const currentUser = 'System';
      const currentUserId = 'public-registration';
      const memberName = `${this.firstName} ${this.surname}`;
      await ActivityLogger.memberInvited(currentUser, currentUserId, memberName, 'Details Page');

      await this.createClientRecord({
        clientId,
        memberId: memberDocRef.id,
        clientType,
        address,
        createdAt,
        formattedDate
      });

      await this.processInviteCompletion(clientId, memberDocRef.id);
      await this.addNewMemberEntry(memberDocRef.id, clientId);

      this.latestClientId = clientId;
      this.latestMemberId = memberDocRef.id;
    },

    async markPendingRegistrationPaid() {
      if (!this.pendingId) return;
      try {
        await setDoc(doc(db, 'pendingRegistrations', this.pendingId), {
          status: 'completed',
          payfast: {
            ...(this.pendingFormData?.payfast || {}),
            payment_status: 'COMPLETE'
          },
          updatedAt: new Date().toISOString(),
          completedAt: new Date().toISOString()
        }, { merge: true });
      } catch (err) {
        console.warn('[Details] Failed to mark pending registration paid:', err);
      }
    },

    async forceCompletePendingRegistration(pendingId) {
      try {
        const now = new Date().toISOString();
        await setDoc(doc(db, 'pendingRegistrations', pendingId), {
          status: 'completed',
          payfast: {
            payment_status: 'COMPLETE',
            amount: '89.00',
            forcedCompletion: true
          },
          updatedAt: now,
          completedAt: now
        }, { merge: true });
        return {
          status: 'completed',
          payfastStatus: 'COMPLETE',
          paymentComplete: true
        };
      } catch (err) {
        console.warn('[Details] Failed to force-complete pending registration:', err);
        return { status: 'completed', paymentComplete: true };
      }
    },

    async createClientRecord({ clientId, memberId, clientType, address, createdAt, formattedDate }) {
      try {
        // Normalize ID number (remove spaces) for consistent storage
        const normalizedIdNumber = (this.idNumber || '').replace(/\s/g, '');

        const membershipLabel = this.inviteData?.membershipType || (clientType === 'business' ? 'Business' : 'Individual');

        // Generate proper CIF number (not the same as clientId)
        let cifNumber;
        try {
          cifNumber = await CIFGenerator.generateCIF(clientType);
        } catch (error) {
          console.error('Error generating CIF number, using fallback:', error);
          cifNumber = CIFGenerator.generateTestCIF(clientType);
        }

        const clientDocRef = doc(db, 'clients', clientId);
        const clientData = {
          clientId,
          cifNumber: cifNumber, // Proper CIF number, not clientId
          firstName: this.firstName,
          surname: this.surname,
          idNumber: normalizedIdNumber,
          phone: this.phone,
          email: this.email,
          address,
          city: this.city,
          postalCode: this.postalCode,
          membership: membershipLabel,
          membershipStatus: 'Active',
          membershipNumber: clientId, // Membership number can be clientId
          clientType,
          status: 'active',
          memberId,
          source: 'Details Page',
          dateAdded: formattedDate,
          createdAt,
          updatedAt: createdAt
        };

        await setDoc(clientDocRef, clientData, { merge: true });

        const currentUser = 'System';
        const currentUserId = 'public-registration';
        await ActivityLogger.clientCreated(currentUser, currentUserId, `${this.firstName} ${this.surname}`, 'Details Page');
      } catch (error) {
        console.error('Error creating client record from Details page:', error);
      }
    },

    async processInviteCompletion(clientId, memberId) {
      const overrides = {
        status: 'Client Completed',
        clientId,
        completedAt: new Date().toISOString(),
        paymentStatus: 'paid'
      };
      if (memberId) {
        overrides.memberId = memberId;
      }

      console.log('[Details] Processing invite completion', { inviteId: this.inviteId, clientId, memberId });

      if (this.inviteId) {
        await this.completeSpecificInvite(this.inviteId, overrides);
        console.log('[Details] Completed specific invite:', this.inviteId);
      } else {
        const emailToMatch = (this.email || '').trim().toLowerCase();
        await this.completeInvitesByEmail(emailToMatch, overrides);
        console.log('[Details] Completed invites by email:', emailToMatch);
      }
    },

    async completeSpecificInvite(inviteId, overrides) {
      try {
        const pendingInviteDoc = await getDoc(doc(db, 'pendingInvites', inviteId));
        if (!pendingInviteDoc.exists()) {
          console.log('[Details] Pending invite not found:', inviteId);
          return;
        }
        const pendingInviteData = pendingInviteDoc.data();
        const completedInviteData = this.buildCompletedInviteData(pendingInviteData, {
          ...overrides,
          originalInviteId: inviteId,
        });

        // Add to completed invites
        await setDoc(doc(collection(db, 'completedInvites')), completedInviteData);
        console.log('[Details] Invite moved to completedInvites collection');

        // Remove from pending invites
        await deleteDoc(doc(db, 'pendingInvites', inviteId));
        console.log('[Details] Invite deleted from pendingInvites collection');

        // Update invite status to completed
        this.inviteStatus = 'Client Completed';
      } catch (error) {
        console.error('[Details] Error moving invite to completed:', error);
      }
    },

    async completeInvitesByEmail(emailToMatch, overrides) {
      if (!emailToMatch) {
        return;
      }
      try {
        const invitesRef = collection(db, 'pendingInvites');
        const pendingSnapshot = await getDocs(query(invitesRef, where('status', '==', 'Pending')));
        const matchingDocs = pendingSnapshot.docs.filter(d => {
          const inviteEmail = (d.data()?.email || '').trim().toLowerCase();
          return inviteEmail && inviteEmail === emailToMatch;
        });

        for (const matchingDoc of matchingDocs) {
          const completedInviteData = this.buildCompletedInviteData(matchingDoc.data(), {
            ...overrides,
            originalInviteId: matchingDoc.id,
          });
          await setDoc(doc(collection(db, 'completedInvites')), completedInviteData);
          console.log(`Invite ${matchingDoc.id} moved to completedInvites collection`);
          await deleteDoc(doc(db, 'pendingInvites', matchingDoc.id));
          console.log(`Invite ${matchingDoc.id} deleted from pendingInvites collection`);
        }

        if (matchingDocs.length > 0) {
          console.log(`Processed ${matchingDocs.length} pending invite(s) - moved to completed and deleted from pending`);
        }
      } catch (error) {
        console.error('Error processing pending invites by email:', error);
      }
    },

    buildCompletedInviteData(sourceData = {}, overrides = {}) {
      const completedInviteData = {
        ...sourceData,
        firstName: this.firstName,
        surname: this.surname,
        idNumber: this.idNumber,
        phone: this.phone,
        email: this.email,
        confirmEmail: this.confirmEmail,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        city: this.city,
        postalCode: this.postalCode,
        address: `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}`,
        termsAccepted: this.termsAccepted,
        completedAt: new Date().toISOString(),
        ...overrides,
      };

      if (!completedInviteData.sessionId && completedInviteData.email) {
        const emailLower = completedInviteData.email.trim().toLowerCase();
        completedInviteData.email = emailLower;
        completedInviteData.sessionId = `user_${md5(emailLower).substring(0, 8)}`;
      }

      if (completedInviteData.sessionId) {
        completedInviteData.detailsUrl = `${window.location.origin}/details/${completedInviteData.sessionId}`;
      }

      return completedInviteData;
    },

    async addNewMemberEntry(memberId, clientId) {
      try {
        const newMemberData = {
          firstName: this.firstName,
          surname: this.surname,
          idNumber: this.idNumber,
          phone: this.phone,
          email: this.email,
          confirmEmail: this.confirmEmail,
          addressLine1: this.addressLine1,
          addressLine2: this.addressLine2,
          city: this.city,
          postalCode: this.postalCode,
          address: `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}`,
          termsAccepted: this.termsAccepted,
          memberType: this.inviteData?.memberType || 'Individual',
          membershipType: this.inviteData?.membershipType || '',
          status: 'Newly Added Member',
          dateAdded: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          createdAt: new Date().toISOString(),
          memberId,
          clientId
        };
        await setDoc(doc(collection(db, 'newMembers')), newMemberData);
        console.log('Added entry to newMembers for Newly Added Member');
      } catch (error) {
        console.error('Error adding to newMembers:', error);
      }
    },
  },

  async handlePaymentReturn(pendingId) {
    console.log('[Details] Handling payment return', { pendingId, sessionKey: this.sessionKey });
    this.step = 2;
    this.pendingId = pendingId;
    this.paymentStatus = 'paid';
    this.paymentStatusMessage = 'Payment successful! Updating your membership...';

    try {
      // If we have sessionKey, update user directly
      if (this.sessionKey) {
        await this.updateUserFromSession();
      }

      // Show success
      this.notificationType = 'success';
      this.notificationTitle = 'Welcome to the Club!';
      this.notificationMessage = 'Your payment has been confirmed and your membership is now active.';
      this.showNotification = true;
      this.step = 3;

      console.log('[Details] Payment return handled successfully');
    } catch (err) {
      console.error('[Details] Error handling payment return:', err);
      this.paymentStatus = 'failed';
      this.paymentStatusMessage = 'Payment successful but there was an issue updating your status.';
    } finally {
      this.initializing = false;
    }
  },

  async updateUserFromSession() {
    try {
      console.log('[Details] Updating user from session:', this.sessionKey);

      // Find user in members collection by email
      const membersRef = collection(db, 'members');
      const memberQuery = query(membersRef, where('email', '==', this.email.toLowerCase()));
      const memberSnapshot = await getDocs(memberQuery);

      if (!memberSnapshot.empty) {
        const memberDoc = memberSnapshot.docs[0];
        const memberData = memberDoc.data();

        // Update member with payment status
        await updateDoc(memberDoc.ref, {
          status: 'active',
          paymentStatus: 'paid',
          subscriptionActive: true,
          updatedAt: new Date().toISOString(),
          completedAt: new Date().toISOString()
        });

        console.log('[Details] Updated member payment status:', memberDoc.id);

        // Process invite completion
        if (this.inviteId) {
          await this.processInviteCompletion(memberData.clientId, memberDoc.id);
        }

        this.latestMemberId = memberDoc.id;
        this.latestClientId = memberData.clientId;
      } else {
        console.warn('[Details] No member found with email:', this.email);
      }
    } catch (error) {
      console.error('[Details] Error updating user from session:', error);
      throw error;
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
}

.details-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0;
  margin: 0;
  width: 100vw;
  overflow-x: hidden;
  overflow-y: visible;
  background-color: #ffffff !important;
  padding-bottom: 160px;
}

.initializing-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.85);
  z-index: 5;
}

/* --- TOP WAVE (flipped mirror of bottom; thinner gold band) --- */
.top-image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 360px;
  z-index: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.top-image-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-position: center top;
  transform: scaleY(-1);
  animation: waveSlideTop 900ms cubic-bezier(0.22, 1, 0.36, 1) both;
  background-image: url("data:image/svg+xml;utf8,\
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 360' preserveAspectRatio='none'>\
  <path d='M0,120 C320,85 760,240 1100,190 C1280,165 1370,150 1440,160 L1440,360 L0,360 Z' fill='%23BCA258'/>\
  <path d='M0,160 C320,135 760,260 1100,205 C1280,180 1370,165 1440,160 L1440,360 L0,360 Z' fill='%23002060'/>\
</svg>");
}

/* --- BOTTOM WAVE --- */
.bottom-image-container {
  position: absolute;
  bottom: -60px;
  left: 0;
  width: 100vw;
  height: 320px;
  z-index: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.bottom-image-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-position: center top;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 360' preserveAspectRatio='none'><rect width='1440' height='360' fill='transparent'/><path d='M0,120 C320,85 760,240 1100,190 C1280,165 1370,150 1440,160 L1440,360 L0,360 Z' fill='%23BCA258'/><path d='M0,160 C320,128 760,270 1100,220 C1280,195 1370,180 1440,190 L1440,360 L0,360 Z' fill='%23002060'/></svg>");
  animation: waveSlideBottom 900ms cubic-bezier(0.22, 1, 0.36, 1) 120ms both;
}

.logo-circle {
  position: absolute;
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  width: 180px;
  height: 180px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

.logo-image {
  width: 180px;
  height: 180px;
}

.content-container {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 160px 20px 40px 20px;
  text-align: center;
}

.step-container {
  text-align: center;
  min-height: 200px;
  margin-top:120px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.brand-welcome {
  font-size: 2.2rem;
  font-weight: 700;
  color: #002060;
  margin-bottom: 16px;
}

.step-description {
  font-size: 1.2rem;
  color: #002060;
  margin-bottom: 28px;
  line-height: 1.75;
  font-weight: 600;
}

.welcome-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: #002060;
  margin-bottom: 0.5rem;
  text-align: center;
}

.invite-status-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  border-left: 4px solid #002060;
}

.invite-info {
  margin-top: 12px;
}

.subscription-status-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.status-label {
  font-weight: 600;
  color: #002060;
}

.status-message {
  margin: 0;
  color: #4a5568;
  font-size: 0.95rem;
}

.invite-type, .invite-membership {
  margin: 4px 0;
  font-size: 0.9rem;
  color: #666;
}

.screen-explanation {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.explanation-text {
  font-size: 0.875rem;
  color: #4a5568;
  line-height: 1.5;
  margin-bottom: 0.75rem;
}

.explanation-text:last-child {
  margin-bottom: 0;
}

.welcome-section {
  text-align: center;
  margin-bottom: 48px;
  padding: 0 20px;
}

.welcome-title {
  font-size: 3rem;
  font-weight: 700;
  color: #002060;
  margin-bottom: 24px;
  line-height: 1.2;
}

.welcome-message {
  font-size: 1.35rem;
  color: #4a5568;
  line-height: 1.8;
  max-width: 680px;
  margin: 0 auto;
  font-weight: 400;
}

.experience-section {
  margin-bottom: 48px;
  padding: 0 20px;
}

.section-intro {
  margin-bottom: 32px;
  text-align: center;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #002060;
  margin-bottom: 16px;
  line-height: 1.3;
  text-align: center;
}

.section-description {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
  font-weight: 400;
}

.fields-container {
  max-width: 800px;
  margin: 0 auto;
}

.field-group {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.field-group:last-child {
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .top-image-container { height: 280px; }
  .bottom-image-container { height: 280px; bottom: -50px; }
  .field-group {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .welcome-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.3rem;
  }
}

.terms-container {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 24px 0;
}

.premium-checkbox {
  margin: 0;
}

.premium-checkbox :deep(.v-label) {
  font-size: 1rem;
  color: #374151;
  line-height: 1.5;
  font-weight: 500;
}

.completion-section {
  text-align: center;
  margin-top: 48px;
  padding: 0 20px;
}

.custom-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: #002060;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 18px 26px;
  cursor: pointer;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.4px;
  box-shadow: 0 10px 26px rgba(0, 32, 96, 0.28);
  transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease;
}

.custom-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px rgba(0, 32, 96, 0.34);
}

.custom-button:active:not(:disabled) {
  transform: translateY(0);
}

.custom-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.completion-btn {
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.2);
  transition: all 0.3s ease;
  padding: 16px 24px;
  border-radius: 6px !important;
}

.completion-btn:hover {
  box-shadow: 0 6px 20px rgba(0, 32, 96, 0.3);
  transform: translateY(-2px);
}

.form-nav {
  display: flex;
  justify-content: center;
  margin-top: 36px;
}

.button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.success-text {
  font-size: 1.8rem;
  color: #002060;
  font-weight: 700;
  margin: 0;
}

.success-message {
  max-width: 500px;
  margin: 0 auto;
}

/* Premium Step Animations */
.step-one {
  animation: fadeUp 700ms ease-out both;
}

.step-three {
  animation: fadeUp 700ms ease-out both;
}

.step-one .brand-welcome {
  animation: softReveal 800ms ease-out 100ms both;
}

.step-one .step-description {
  animation: softReveal 900ms ease-out 250ms both;
}

.start-journey-btn {
  font-size: 1rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.4px;
  box-shadow: 0 8px 22px rgba(0, 32, 96, 0.25);
  transition: transform 220ms ease, box-shadow 220ms ease;
  padding: 18px 26px;
  border-radius: 6px !important;
}

.start-journey-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(0, 32, 96, 0.32);
}

.start-journey-btn:active {
  transform: translateY(0);
}

.success-text {
  animation: softReveal 800ms ease-out 100ms both;
}

.success-message {
  animation: softReveal 900ms ease-out 250ms both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes softReveal {
  from { opacity: 0; filter: blur(4px); transform: translateY(6px); }
  to { opacity: 1; filter: blur(0); transform: translateY(0); }
}

/* Wave entrance animations */
@keyframes waveSlideTop {
  0% { transform: translateY(-50px) scaleY(-1); opacity: 0; }
  60% { transform: translateY(8px) scaleY(-1); }
  100% { transform: translateY(0) scaleY(-1); opacity: 1; }
}

@keyframes waveSlideBottom {
  0% { transform: translateY(50px); opacity: 0; }
  60% { transform: translateY(-6px); }
  100% { transform: translateY(0); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .top-image-bg,
  .bottom-image-bg {
    animation: none !important;
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Custom Checkbox and Terms Link Styles */
.custom-checkbox-container {
  margin-bottom: 16px;
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
  accent-color: #002060;
}

.custom-checkbox.error {
  border: 2px solid #e53e3e;
}

.checkbox-label {
  font-size: 1rem;
  line-height: 1.5;
  color: #333;
  cursor: pointer;
  user-select: none;
  margin: 0;
  flex: 1;
}

.terms-link {
  color: #002060;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
  transition: color 0.2s ease;
}

.terms-link:hover {
  color: #001a4d;
  text-decoration: none;
}

.error-message {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 4px;
  margin-left: 32px;
}
</style>
